import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { HttpServiceService } from '../../services/http-service.service';
import { LoadingService } from '../../services/loading.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgxToastrService } from '../../_services/ngx-toastr/ngx-toastr.service';
import { Select2Data } from 'ng-select2-component';
import { Subject, debounceTime, tap, switchMap, Subscription, interval, takeWhile } from 'rxjs';
import { NgIf, NgFor, NgClass, DatePipe } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { BreadcrumbComponent } from '../../elements/breadcrumb/breadcrumb.component';
import { PaginationComponent } from '../../elements/pagination/pagination.component';
import { USER_PERMISSIONS } from '../../app.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  InspectionReportRow,
  InspectionByManagerRow,
  InspectionReportSummary,
  ReportJobStatusResponse
} from '../../models/reports.model';

@Component({
  selector: 'app-inspection-reports',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    PaginationComponent,
    BreadcrumbComponent,
    NgSelectModule,
    NgIf,
    NgFor,
    NgClass,
    DatePipe
  ],
  templateUrl: './inspection-reports.component.html',
  styleUrl: './inspection-reports.component.css'
})
export class InspectionReportsComponent implements OnInit, OnDestroy {
  breadcrumbList = {
    breadcrumb_path: 'Reports',
    currentURL: 'Inspections',
    breadcrumb_url: '/admin/reports/inspections'
  };

  // Tab control
  activeTab: 'all' | 'by-manager' | 'graphs' = 'all';

  // Pagination - All tab
  pageAll: number = 1;
  totalRowsAll: number = 20;
  totalPageAll: number = 0;
  pageCountAll: number = 1;

  // Pagination - By Manager tab
  pageByManager: number = 1;
  totalRowsByManager: number = 20;
  totalPageByManager: number = 0;
  pageCountByManager: number = 1;

  // Data
  allInspectionsData: InspectionReportRow[] = [];
  byManagerData: InspectionByManagerRow[] = [];
  summaryData: InspectionReportSummary | null = null;

  // Filter form
  filterForm!: FormGroup;
  disableClear: boolean = false;

  // Property dropdown
  propertiesList: any[] = [];
  propertiesItems: Select2Data = [];
  searchProperty$ = new Subject<string>();
  private propertyPage = 1;
  private propertyPageSize = 20;
  private hasMoreProperties = true;

  // Manager dropdown
  managersList: any[] = [];
  managersItems: Select2Data = [];
  searchManager$ = new Subject<string>();
  private managerPage = 1;
  private managerPageSize = 20;
  private hasMoreManagers = true;

  // Status options
  statusOptions = [
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Submitted', label: 'Submitted' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Tenant Review', label: 'Tenant Review' },
    { value: 'Owner Review', label: 'Owner Review' }
  ];

  // Inspection type options
  inspectionTypeOptions = [
    { value: 'Move In Inspection', label: 'Move In Inspection' },
    { value: 'Move Out Inspection', label: 'Move Out Inspection' },
    { value: 'Quarterly Inspection', label: 'Quarterly Inspection' },
    { value: 'Q1', label: 'Q1' },
    { value: 'Q2', label: 'Q2' },
    { value: 'Q3', label: 'Q3' },
    { value: 'Q4', label: 'Q4' },
  ];

  // Download state
  isDownloading: boolean = false;
  downloadProgress: string = '';
  activeDownloadType: 'pdf' | 'csv' | 'email' | 'whatsapp' | null = null;

  // Share modal state
  showShareModal: boolean = false;
  shareType: 'email' | 'whatsapp' = 'email';
  shareForm!: FormGroup;

  // Services
  private httpService = inject(HttpServiceService);
  private authService = inject(LocalStorageService);
  loadingService = inject(LoadingService);
  private destroyRef = inject(DestroyRef);

  // Subscriptions
  private allReportSubscription?: Subscription;
  private byManagerSubscription?: Subscription;
  private propertySearchSubscription?: Subscription;
  private managerSearchSubscription?: Subscription;

  constructor(
    private fb: FormBuilder,
    private alertService: NgxToastrService
  ) {
    this.initFilterForm();
    this.initShareForm();
  }

  ngOnInit(): void {
    this.setupPropertySearch();
    this.setupManagerSearch();
    this.setupDateValidation();
    this.loadAllReport();
    this.loadByManagerReport();
  }

  private initFilterForm(): void {
    const defaults = this.defaultFilterValues();
    this.filterForm = this.fb.group({
      start_date: [defaults.start_date],
      end_date: [{ value: defaults.end_date, disabled: !defaults.start_date }],
      managed_by: [[]],
      property_id: [null],
      status: [null],
      inspection_type: [defaults.inspection_type]
    });
  }

  /**
   * Default filters: the current financial-year quarter (Q1 = Apr–Jun,
   * Q2 = Jul–Sep, Q3 = Oct–Dec, Q4 = Jan–Mar) with the matching inspection type.
   */
  private defaultFilterValues(): {
    start_date: string;
    end_date: string;
    managed_by: string[];
    property_id: null;
    status: null;
    inspection_type: string;
  } {
    const today = new Date();
    const month = today.getMonth(); // 0-based
    const year = today.getFullYear();
    // Months since April (financial-year start), wrapping Jan–Mar to 9–11.
    const fyMonthIndex = (month + 9) % 12;
    const quarter = Math.floor(fyMonthIndex / 3) + 1;
    const startMonth = (quarter - 1) * 3 + 3; // 3 = April
    const startYear = month < 3 ? year - 1 : year;
    const start = new Date(startYear, startMonth, 1);
    const end = new Date(startYear, startMonth + 3, 0);
    return {
      start_date: this.toIsoDate(start),
      end_date: this.toIsoDate(end),
      managed_by: [],
      property_id: null,
      status: null,
      inspection_type: `Q${quarter}`
    };
  }

  private toIsoDate(d: Date): string {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  }

  private initShareForm(): void {
    this.shareForm = this.fb.group({
      selectedUser: [null],
      file_type: ['pdf'],
      first_name: [''],
      last_name: [''],
      email: [''],
      country_code: [''],
      whatsapp: ['']
    });
  }

  // Report type options for share modal
  reportTypeOptions = [
    { value: 'pdf', label: 'PDF' },
    { value: 'csv', label: 'CSV' }
  ];

  private setupDateValidation(): void {
    this.filterForm.get('start_date')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const endDateControl = this.filterForm.get('end_date');
        if (value) {
          endDateControl!.enable();
        } else {
          endDateControl!.disable();
          endDateControl!.reset();
        }
      });
  }

  private setupPropertySearch(): void {
    this.propertySearchSubscription = this.searchProperty$
      .pipe(
        debounceTime(300),
        tap(() => {
          this.propertyPage = 1;
          this.hasMoreProperties = true;
        }),
        switchMap((term) => {
          const query = term ? `&name=${term}` : '';
          return this.httpService.getAllProperties(this.propertyPage, this.propertyPageSize, query);
        })
      )
      .subscribe((response) => {
        this.propertiesList = response.data?.properties ?? [];
        this.hasMoreProperties = this.propertiesList.length < (response.data?.total ?? 0);
        this.propertiesItems = this.propertiesList.map((p: any) => ({
          value: p.uid,
          label: p.name,
          data: { name: p.name }
        }));
      });
    this.searchProperty$.next('');
  }

  private setupManagerSearch(): void {
    this.managerSearchSubscription = this.searchManager$
      .pipe(
        debounceTime(300),
        tap(() => {
          this.managerPage = 1;
          this.hasMoreManagers = true;
        }),
        switchMap((term) => {
          const search = term ? term : '';
          return this.httpService.getPropertyManagers(this.managerPage, this.managerPageSize, search);
        })
      )
      .subscribe((response) => {
        this.managersList = response.data?.users ?? [];
        this.hasMoreManagers = this.managersList.length < (response.data?.total ?? 0);
        this.managersItems = this.managersList.map((m: any) => ({
          value: m.uid,
          label: `${m.first_name} ${m.last_name}`,
          data: {
            first_name: m.first_name,
            last_name: m.last_name,
            email: m.email,
            phone: m.phone,
            country_code: m.country_code
          }
        }));
      });
    this.searchManager$.next('');
  }

  onShareUserChange(selectedItem: any): void {
    if (selectedItem && selectedItem.data) {
      // Populate first_name and last_name
      this.shareForm.patchValue({
        first_name: selectedItem.data.first_name || '',
        last_name: selectedItem.data.last_name || ''
      });

      if (this.shareType === 'email') {
        this.shareForm.patchValue({ email: selectedItem.data.email || '' });
      } else {
        this.shareForm.patchValue({
          country_code: selectedItem.data.country_code || '',
          whatsapp: selectedItem.data.phone || ''
        });
      }
    }
  }

  onPropertyScrollEnd(): void {
    if (!this.hasMoreProperties) return;
    this.propertyPage++;
    this.httpService.getAllProperties(this.propertyPage, this.propertyPageSize)
      .subscribe((resp) => {
        const more = resp.data?.properties ?? [];
        this.propertiesList = [...this.propertiesList, ...more];
        this.hasMoreProperties = this.propertiesList.length < (resp.data?.total ?? 0);
        this.propertiesItems = this.propertiesList.map((p: any) => ({
          value: p.uid,
          label: p.name,
          data: { name: p.name }
        }));
      });
  }

  onManagerScrollEnd(): void {
    if (!this.hasMoreManagers) return;
    this.managerPage++;
    this.httpService.getPropertyManagers(this.managerPage, this.managerPageSize)
      .subscribe((resp) => {
        const more = resp.data?.users ?? [];
        this.managersList = [...this.managersList, ...more];
        this.hasMoreManagers = this.managersList.length < (resp.data?.total ?? 0);
        this.managersItems = this.managersList.map((m: any) => ({
          value: m.uid,
          label: `${m.first_name} ${m.last_name}`,
          data: {
            first_name: m.first_name,
            last_name: m.last_name,
            email: m.email,
            phone: m.phone,
            country_code: m.country_code
          }
        }));
      });
  }

  switchTab(tab: 'all' | 'by-manager' | 'graphs'): void {
    this.activeTab = tab;
  }

  /** The Manager filter is multi-select; normalise whatever ng-select hands back to a uid list. */
  private managerIdsFromValue(value: any): string[] {
    const list = Array.isArray(value) ? value : value ? [value] : [];
    return list
      .map((v: any) => (v && typeof v === 'object' ? v.value : v))
      .filter((v: any) => !!v);
  }

  private buildFilterQuery(): string {
    let query = '';
    const formValue = this.filterForm.getRawValue();

    if (formValue.start_date) {
      query += `&start_date=${formValue.start_date}`;
    }
    if (formValue.end_date) {
      query += `&end_date=${formValue.end_date}`;
    }
    const managerIds = this.managerIdsFromValue(formValue.managed_by);
    if (managerIds.length) query += `&managed_by=${managerIds.join(',')}`;
    if (formValue.property_id) {
      const propertyId = typeof formValue.property_id === 'object' ? formValue.property_id.value : formValue.property_id;
      if (propertyId) query += `&property_id=${propertyId}`;
    }
    if (formValue.status) {
      query += `&status=${formValue.status}`;
    }
    if (formValue.inspection_type) {
      query += `&inspection_type=${formValue.inspection_type}`;
    }

    return query;
  }

  loadAllReport(): void {
    const query = this.buildFilterQuery();
    this.allReportSubscription = this.httpService
      .getInspectionReportAll(this.pageAll, this.totalRowsAll, query)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.allInspectionsData = response.data.rows ?? [];
            this.summaryData = response.data.summary ?? null;
            this.totalPageAll = response.data.total ?? 0;
            this.pageCountAll = response.data.pageCount ?? 1;
          }
        },
        error: () => {
          this.alertService.error('Failed to load inspection report', 'toast-top-right');
        }
      });
  }

  loadByManagerReport(): void {
    const query = this.buildFilterQuery();
    this.byManagerSubscription = this.httpService
      .getInspectionReportByManager(this.pageByManager, this.totalRowsByManager, query)
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.byManagerData = response.data.rows ?? [];
            if (!this.summaryData) {
              this.summaryData = response.data.summary ?? null;
            }
            this.totalPageByManager = response.data.total ?? 0;
            this.pageCountByManager = response.data.pageCount ?? 1;
          }
        },
        error: () => {
          this.alertService.error('Failed to load by-manager report', 'toast-top-right');
        }
      });
  }

  onFilterChange(): void {
    this.disableClear = true;
    this.pageAll = 1;
    this.pageByManager = 1;
    this.loadAllReport();
    this.loadByManagerReport();
  }

  resetFilters(): void {
    this.filterForm.reset(this.defaultFilterValues());
    this.disableClear = false;
    this.pageAll = 1;
    this.pageByManager = 1;
    this.loadAllReport();
    this.loadByManagerReport();
  }

  pageChangeAll(page: number): void {
    if (this.pageAll === page) return;
    this.pageAll = page;
    this.loadAllReport();
  }

  pageChangeByManager(page: number): void {
    if (this.pageByManager === page) return;
    this.pageByManager = page;
    this.loadByManagerReport();
  }

  // Download functionality with polling
  downloadReport(fileType: 'pdf' | 'csv'): void {
    if (this.isDownloading) return;

    this.isDownloading = true;
    this.activeDownloadType = fileType;
    this.downloadProgress = 'Initiating...';

    const query = this.buildFilterQuery();
    const downloadFn = this.activeTab === 'all' || this.activeTab === 'graphs'
      ? this.httpService.downloadInspectionReportAll(fileType, query)
      : this.httpService.downloadInspectionReportByManager(fileType, query);

    downloadFn.subscribe({
      next: (response) => {
        if (response.success && response.data?.job_id) {
          this.pollJobStatus(response.data.job_id);
        } else {
          this.resetDownloadState();
          this.alertService.error('Failed to initiate download', 'toast-top-right');
        }
      },
      error: () => {
        this.resetDownloadState();
        this.alertService.error('Failed to initiate download', 'toast-top-right');
      }
    });
  }

  private resetDownloadState(): void {
    this.isDownloading = false;
    this.downloadProgress = '';
    this.activeDownloadType = null;
  }

  private pollJobStatus(jobId: string): void {
    const maxPollingTime = 30000; // 30 seconds
    const pollingInterval = 1000; // 1 second
    const startTime = Date.now();
    let pollCount = 0;

    // Make immediate first poll
    this.checkJobStatus(jobId);

    const poll$ = interval(pollingInterval).pipe(
      takeWhile(() => {
        const elapsed = Date.now() - startTime;
        return elapsed < maxPollingTime && this.isDownloading;
      })
    );

    poll$.subscribe({
      next: () => {
        pollCount++;
        this.downloadProgress = `Checking... (${pollCount})`;
        this.checkJobStatus(jobId);
      },
      complete: () => {
        if (this.isDownloading) {
          this.resetDownloadState();
          this.alertService.error('Report generation timed out. Please try again.', 'toast-top-right');
        }
      }
    });
  }

  private checkJobStatus(jobId: string): void {
    this.httpService.getReportJobStatus(jobId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const jobData: ReportJobStatusResponse = response.data;

          if (jobData.status === 'completed' && jobData.file_url) {
            this.resetDownloadState();
            this.alertService.success('Report generated successfully!', 'toast-top-right');
            window.open(jobData.file_url, '_blank');
          } else if (jobData.status === 'failed') {
            this.resetDownloadState();
            this.alertService.error(jobData.error_message || 'Report generation failed', 'toast-top-right');
          }
        }
      },
      error: () => {
        // Continue polling on error
      }
    });
  }

  // Share modal methods
  openShareModal(type: 'email' | 'whatsapp'): void {
    this.shareType = type;
    this.shareForm.reset({ file_type: 'pdf' });
    this.showShareModal = true;
  }

  closeShareModal(): void {
    this.showShareModal = false;
    this.shareForm.reset({ file_type: 'pdf' });
  }

  submitShare(): void {
    const formValue = this.shareForm.getRawValue();
    const shareValue = this.shareType === 'email'
      ? formValue.email
      : formValue.whatsapp;

    if (!shareValue || shareValue.trim() === '') {
      this.alertService.error(`Please enter ${this.shareType === 'email' ? 'an email address' : 'a WhatsApp number'}`, 'toast-top-right');
      return;
    }

    this.closeShareModal();
    this.isDownloading = true;
    this.activeDownloadType = this.shareType;
    this.downloadProgress = 'Sending...';

    const query = this.buildFilterQuery();

    // Build share data with share_type, first_name and last_name
    const shareData: any = {
      share_type: this.shareType,
      first_name: formValue.first_name?.trim() || '',
      last_name: formValue.last_name?.trim() || ''
    };

    if (this.shareType === 'email') {
      shareData.email = shareValue.trim();
    } else {
      shareData.country_code = formValue.country_code?.trim() || '';
      shareData.whatsapp = shareValue.trim();
    }

    const fileType = formValue.file_type || 'pdf';
    const downloadFn = this.activeTab === 'all' || this.activeTab === 'graphs'
      ? this.httpService.downloadInspectionReportAll(fileType, query, shareData)
      : this.httpService.downloadInspectionReportByManager(fileType, query, shareData);

    downloadFn.subscribe({
      next: (response) => {
        if (response.success && response.data?.job_id) {
          this.pollJobStatusForShare(response.data.job_id);
        } else {
          this.resetDownloadState();
          this.alertService.error('Failed to initiate share', 'toast-top-right');
        }
      },
      error: () => {
        this.resetDownloadState();
        this.alertService.error('Failed to initiate share', 'toast-top-right');
      }
    });
  }

  private pollJobStatusForShare(jobId: string): void {
    const maxPollingTime = 30000;
    const pollingInterval = 1000;
    const startTime = Date.now();
    let pollCount = 0;

    // Make immediate first poll
    this.checkJobStatusForShare(jobId);

    const poll$ = interval(pollingInterval).pipe(
      takeWhile(() => {
        const elapsed = Date.now() - startTime;
        return elapsed < maxPollingTime && this.isDownloading;
      })
    );

    poll$.subscribe({
      next: () => {
        pollCount++;
        this.downloadProgress = `Processing... (${pollCount})`;
        this.checkJobStatusForShare(jobId);
      },
      complete: () => {
        if (this.isDownloading) {
          this.resetDownloadState();
          this.alertService.error('Share request timed out. Please try again.', 'toast-top-right');
        }
      }
    });
  }

  private checkJobStatusForShare(jobId: string): void {
    this.httpService.getReportJobStatus(jobId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          const jobData: ReportJobStatusResponse = response.data;

          if (jobData.status === 'completed') {
            this.resetDownloadState();
            this.alertService.success(`Report shared via ${this.shareType} successfully!`, 'toast-top-right');
          } else if (jobData.status === 'failed') {
            this.resetDownloadState();
            this.alertService.error(jobData.error_message || 'Failed to share report', 'toast-top-right');
          }
        }
      },
      error: () => {
        // Continue polling on error
      }
    });
  }

  // Permission check
  get checkDownloadPermission(): boolean {
    return this.authService.checkPermission(USER_PERMISSIONS.REPORTS.REPORTS_DOWNLOAD);
  }

  // Status badge class
  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'In Progress': 'badge-warning',
      'Submitted': 'badge-info',
      'Completed': 'badge-success',
      'Tenant Review': 'badge-primary',
      'Owner Review': 'badge-secondary'
    };
    return statusClasses[status] || 'badge-secondary';
  }

  // Get summary keys for iteration
  getSummaryKeys(obj: { [key: string]: number } | undefined): string[] {
    return obj ? Object.keys(obj) : [];
  }

  // Math helper for template
  Math = Math;

  ngOnDestroy(): void {
    this.allReportSubscription?.unsubscribe();
    this.byManagerSubscription?.unsubscribe();
    this.propertySearchSubscription?.unsubscribe();
    this.managerSearchSubscription?.unsubscribe();
  }
}
